import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { branding } from '../constants/branding';
import axiosInstance from '../services/axiosConfig';
import { useAppSelector } from '../store/hooks';

interface Payment {
  initiatingParty: string;
  debtorIban: string;
  debtorName: string;
  amount: number;
  creditorIban: string;
  creditorName: string;
  currency: string;
  transactionDate: string;
  paymentDetails1: string;
  paymentDetails2: string;
}

interface BatchFile {
  name: string;
  payments: Payment[];
  totalAmount: number;
  totalPayments: number;
}

const Batch: React.FC = () => {
  const [batches, setBatches] = useState<BatchFile[]>([]);
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [processing, setProcessing] = useState(false);
  const [batchResult, setBatchResult] = useState<any>(null);
  const [batchError, setBatchError] = useState<string | null>(null);

  const profile = useAppSelector((state) => state.profile.profile);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // Read as text and handle both comma and semicolon delimiters
          const text = new TextDecoder('utf-8').decode(new Uint8Array(e.target?.result as ArrayBuffer));
          // Replace semicolons with commas for easier parsing
          const normalized = text.replace(/;/g, ',');
          // Parse with XLSX
          const workbook = XLSX.read(normalized, { type: 'string' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const json = XLSX.utils.sheet_to_json<any>(worksheet);

          const parsedPayments: Payment[] = json.map((row: any) => {
            const rawCreditorIban = row['Creditor Iban No'] || row['creditorIban'] || row['toAccountNumber'] || '';
            const creditorIban = typeof rawCreditorIban === 'number'
              ? String(rawCreditorIban).padStart(16, '0')
              : String(rawCreditorIban).padStart(16, '0');
            const payment: Payment = {
              initiatingParty: row['Initiating Party'] || row['initiatingParty'] || '',
              debtorIban: row['Debtor Iban No'] || row['debtorIban'] || '',
              debtorName: row['Debtor Name'] || row['debtorName'] || '',
              amount: parseFloat(row.Amount || row['amount'] || '0'),
              creditorIban,
              creditorName: row['Creditor Name'] || row['creditorName'] || '',
              currency: row.Currency || row['currency'] || '',
              transactionDate: row['Transaction Value Date'] || row['transactionDate'] || '',
              paymentDetails1: row['Payment Details1'] || row['paymentDetails1'] || row['description'] || '',
              paymentDetails2: row['Payment Details2'] || row['paymentDetails2'] || '',
            };
            if (!payment.creditorIban || isNaN(payment.amount) || !payment.currency) {
              throw new Error('Invalid data format in the uploaded file. It must contain "Creditor Iban No", "Amount", and "Currency" columns.');
            }
            return payment;
          });
          
          const newBatch: BatchFile = {
            name: file.name,
            payments: parsedPayments,
            totalAmount: parsedPayments.reduce((sum, p) => sum + p.amount, 0),
            totalPayments: parsedPayments.length
          };

          setBatches(prev => [...prev, newBatch]);
          setSelectedBatchIndex(batches.length);
          setError(null);
        } catch (err) {
          setError('Failed to parse the file. Please ensure it is a valid CSV or Excel file with columns: "Creditor Iban No", "Amount", "Currency", and "Payment Details1".');
        }
      };
      reader.readAsArrayBuffer(file);
    });
  }, [batches.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    },
  });
  
  const removeBatch = (index: number) => {
    setBatches(prev => prev.filter((_, i) => i !== index));
    if (selectedBatchIndex === index) {
      setSelectedBatchIndex(null);
    } else if (selectedBatchIndex && selectedBatchIndex > index) {
      setSelectedBatchIndex(selectedBatchIndex - 1);
    }
  };

  const selectedBatch = selectedBatchIndex !== null ? batches[selectedBatchIndex] : null;

  const handleProcessPayments = () => {
    if (selectedBatch) {
      setShowConfirmation(true);
    }
  };

  const confirmProcessPayments = () => {
    setShowConfirmation(false);
    setOtp('');
    setBatchError(null);
    setShowOtpModal(true);
  };

  const handleVerifyOtpAndProcess = async () => {
    if (otp !== '123456') {
      setBatchError('Invalid OTP. Please try again.');
      setOtp('');
      return;
    }
    if (!selectedBatch) return;
    setProcessing(true);
    setBatchError(null);
    setBatchResult(null);
    try {
      // Always build the correct CSV for backend
      const csvHeader = 'toAccountNumber,amount,description';
      const csvRows = selectedBatch.payments.map(p => `${String(p.creditorIban)},${String(p.amount)},${p.paymentDetails1}`);
      const requestBody = {
        fromAccountNumber: profile?.accountNumber,
        csvData: [csvHeader, ...csvRows].join('\n')
      };
      const response = await axiosInstance.post('/batch-payments', requestBody);
      setBatchResult(response.data);
      removeBatch(selectedBatchIndex!);
      setShowOtpModal(false);
      alert('Batch processed successfully!');
    } catch (err: any) {
      setBatchError(err.response?.data?.message || err.message || 'Batch payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: branding.fontFamily,
      padding: isMobile ? '24px 16px 120px' : '48px 24px',
      background: branding.pageBackground,
    }}>
      <div style={{
        maxWidth: branding.viewport.mainMaxWidth,
        margin: '0 auto',
      }}>
        <h1 style={{
          color: branding.textColor,
          fontWeight: 700,
          fontSize: isMobile ? 24 : 28,
          marginBottom: 32,
        }}>Batch Payments</h1>

        <div {...getRootProps()} style={{
          padding: isMobile ? '32px 16px' : '48px 24px',
          border: `2px dashed ${isDragActive ? branding.primaryColor : '#ccc'}`,
          borderRadius: 20,
          textAlign: 'center',
          cursor: 'pointer',
          background: isDragActive ? 'rgba(10, 132, 255, 0.05)' : '#fff',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          marginBottom: 32,
          transition: 'all 0.2s',
        }}>
          <input {...getInputProps()} />
          {isDragActive ?
            <p style={{ color: branding.primaryColor, fontSize: 18, fontWeight: 600 }}>Drop the files here ...</p> :
            <p style={{ color: '#666', fontSize: 18 }}>Drag 'n' drop a CSV or Excel file here, or click to select a file</p>
          }
          <p style={{ color: '#999', fontSize: 14 }}>File must contain: Creditor Name, Amount, Currency, Payment Details1</p>
        </div>

        {error && <div style={{
          color: branding.errorColor,
          background: 'rgba(255, 0, 0, 0.1)',
          padding: '16px',
          borderRadius: 12,
          marginBottom: 24,
          fontWeight: 500,
        }}>{error}</div>}
        
        {batches.length > 0 && (
          <div style={{
            background: '#fff',
            borderRadius: 20,
            padding: isMobile ? '24px 16px' : 32,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            marginBottom: 32,
          }}>
            <h2 style={{ color: branding.textColor, fontSize: isMobile ? 20 : 22, fontWeight: 600, marginBottom: 24 }}>Uploaded Batches</h2>
            {batches.map((batch, index) => (
              <div 
                key={index}
                onClick={() => setSelectedBatchIndex(index)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 20px',
                  borderRadius: 14,
                  border: `1.5px solid ${selectedBatchIndex === index ? branding.primaryColor : '#eee'}`,
                  background: selectedBatchIndex === index ? '#f7fafd' : '#fff',
                  cursor: 'pointer',
                  marginBottom: 12,
                  transition: 'all 0.2s',
                  boxShadow: selectedBatchIndex === index ? '0 2px 8px rgba(10,132,255,0.10)' : 'none',
              }}>
                <div style={{ paddingRight: 16 }}>
                  <div style={{ fontWeight: 600, color: branding.textColor, marginBottom: 4, wordBreak: 'break-word' }}>{batch.name}</div>
                  <div style={{ fontSize: 14, color: '#666' }}>
                    {batch.totalPayments} records • Total: {batch.totalAmount.toFixed(2)} {batch.payments[0]?.currency}
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); removeBatch(index); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: branding.errorColor,
                    fontSize: 20,
                    cursor: 'pointer',
                    padding: '4px 8px'
                  }}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        {selectedBatch && (
          <>
            <div style={{
              background: '#fff',
              borderRadius: 20,
              padding: isMobile ? '24px 16px' : 32,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
              marginBottom: 32,
            }}>
              <h2 style={{
                color: branding.textColor,
                fontSize: isMobile ? 20 : 22,
                fontWeight: 600,
                marginBottom: 24,
                wordBreak: 'break-word',
              }}>Payment Details for {selectedBatch.name}</h2>
              <div style={{
                overflowX: 'auto',
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: isMobile ? 320 : 'auto',
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #eee' }}>
                      <th style={{ padding: isMobile ? '12px 8px' : '12px 16px', textAlign: 'left', color: '#666', whiteSpace: 'nowrap', fontSize: isMobile ? 13 : 14 }}>Recipient</th>
                      <th style={{ padding: isMobile ? '12px 8px' : '12px 16px', textAlign: 'right', color: '#666', whiteSpace: 'nowrap', fontSize: isMobile ? 13 : 14 }}>Amount</th>
                      <th style={{ padding: isMobile ? '12px 8px' : '12px 16px', textAlign: 'left', color: '#666', whiteSpace: 'nowrap', fontSize: isMobile ? 13 : 14 }}>Currency</th>
                      <th style={{ padding: isMobile ? '12px 8px' : '12px 16px', textAlign: 'left', color: '#666', whiteSpace: 'nowrap', fontSize: isMobile ? 13 : 14 }}>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBatch.payments.map((p, i) => (
                      <React.Fragment key={i}>
                        <tr 
                          onClick={() => setExpandedRow(expandedRow === i ? null : i)}
                          style={{ cursor: 'pointer', background: expandedRow === i ? '#f7fafd' : 'transparent', borderBottom: '1px solid #f7f7f7' }}
                        >
                          <td style={{ padding: isMobile ? '12px 8px' : '12px 16px', fontWeight: 500, color: branding.textColor, fontSize: isMobile ? 14 : 'inherit' }}>{p.creditorName}</td>
                          <td style={{ padding: isMobile ? '12px 8px' : '12px 16px', textAlign: 'right', fontWeight: 600, color: branding.primaryColor, fontSize: isMobile ? 14 : 'inherit', whiteSpace: 'nowrap' }}>{p.amount.toFixed(2)}</td>
                          <td style={{ padding: isMobile ? '12px 8px' : '12px 16px', color: '#666', fontSize: isMobile ? 14 : 'inherit' }}>{p.currency}</td>
                          <td style={{ padding: isMobile ? '12px 8px' : '12px 16px', color: '#666', fontSize: isMobile ? 14 : 'inherit' }}>{p.paymentDetails1}</td>
                        </tr>
                        {expandedRow === i && (
                          <tr style={{ background: '#f7fafd' }}>
                            <td colSpan={4} style={{ padding: '16px 24px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Debtor Name</div>
                                  <div style={{ fontWeight: 600 }}>{p.debtorName}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Debtor IBAN</div>
                                  <div style={{ fontWeight: 600 }}>{p.debtorIban}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Creditor IBAN</div>
                                  <div style={{ fontWeight: 600 }}>{p.creditorIban}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Value Date</div>
                                  <div style={{ fontWeight: 600 }}>{p.transactionDate}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Initiating Party</div>
                                  <div style={{ fontWeight: 600 }}>{p.initiatingParty}</div>
                                </div>
                                <div>
                                  <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>Payment Details 2</div>
                                  <div style={{ fontWeight: 600 }}>{p.paymentDetails2}</div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: isMobile ? 20 : 0,
              background: '#fff',
              borderRadius: 20,
              padding: isMobile ? 24 : 32,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}>
              <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                <div style={{ fontSize: isMobile ? 14 : 16, color: '#666', marginBottom: 4 }}>Total Payments</div>
                <div style={{ fontSize: isMobile ? 32 : 24, fontWeight: 700, color: branding.textColor }}>{selectedBatch.totalPayments}</div>
              </div>
              <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                <div style={{ fontSize: isMobile ? 14 : 16, color: '#666', marginBottom: 4 }}>Total Amount</div>
                <div style={{ fontSize: isMobile ? 32 : 24, fontWeight: 700, color: branding.primaryColor }}>
                  {selectedBatch.totalAmount.toFixed(2)} {selectedBatch.payments[0]?.currency}
                </div>
              </div>
              <button
                onClick={handleProcessPayments}
                style={{
                  padding: '16px 32px',
                  background: branding.primaryColor,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 18,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                  transition: 'all 0.2s',
                  width: isMobile ? '100%' : 'auto',
                }}
              >
                Process Payments
              </button>
            </div>
          </>
        )}
      </div>

      {showConfirmation && selectedBatch && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: isMobile ? '32px 24px' : 40,
            maxWidth: 480,
            width: '100%',
            textAlign: 'center',
          }}>
            <h3 style={{
              fontSize: 24,
              fontWeight: 700,
              color: branding.textColor,
              marginBottom: 16,
            }}>Confirm Batch Payment</h3>
            <p style={{
              fontSize: 16,
              color: '#666',
              marginBottom: 32,
            }}>
              Are you sure you want to process {selectedBatch.totalPayments} payments totaling {selectedBatch.totalAmount.toFixed(2)} {selectedBatch.payments[0]?.currency} from file {selectedBatch.name}?
            </p>
            <div style={{ display: 'flex', gap: 16 }}>
              <button
                onClick={() => setShowConfirmation(false)}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  background: '#f7fafd',
                  border: '1.5px solid #eee',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#666',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmProcessPayments}
                style={{
                  flex: 1,
                  padding: '16px 0',
                  background: branding.primaryColor,
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Confirm & Process
              </button>
            </div>
          </div>
        </div>
      )}

      {showOtpModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1010, padding: 24,
        }}>
          <div style={{
            background: '#fff', borderRadius: 24, padding: isMobile ? 24 : 40,
            maxWidth: 480, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: 0, marginBottom: 16, color: branding.textColor,
              fontSize: isMobile ? 20 : 24, fontWeight: 700,
            }}>Enter OTP</h3>
            <p style={{ margin: '0 auto 32px auto', color: '#666', maxWidth: 320 }}>
              An authorization code was sent to your phone. (Hint: 123456)
            </p>
            <input
              type="tel"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
              maxLength={6}
              style={{
                width: '100%',
                padding: '16px 0',
                fontSize: 32,
                fontWeight: 700,
                textAlign: 'center',
                letterSpacing: isMobile ? 8 : 16,
                border: '2px solid #eee',
                borderRadius: 12,
                marginBottom: 32,
                color: branding.textColor,
                boxSizing: 'border-box',
              }}
            />
            {batchError && <div style={{ color: branding.errorColor, marginBottom: 16 }}>{batchError}</div>}
            <div style={{ display: 'flex', gap: 16 }}>
              <button
                onClick={() => setShowOtpModal(false)}
                style={{
                  flex: 1, padding: '16px 0', background: '#fff',
                  border: '1.5px solid #eee', borderRadius: 12, fontSize: 16,
                  fontWeight: 600, color: '#666', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOtpAndProcess}
                disabled={processing}
                style={{
                  flex: 1, padding: '16px 0', background: processing ? '#eee' : branding.primaryColor,
                  border: 'none', borderRadius: 12, fontSize: 16,
                  fontWeight: 600, color: processing ? '#999' : '#fff', cursor: processing ? 'not-allowed' : 'pointer',
                }}
              >
                {processing ? 'Processing...' : 'Verify & Process'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Batch; 